import React, { useState } from 'react';
import { Gap, Icon, Text } from '@common/components';
import { Colors, FontSize, FontWeight, Radius, Spacing } from '@common/tokens';
import type {
  ScheduleDropValidityType,
  ScheduleShiftType,
} from '@load-balancing/types';
import { startDragGhost } from '@load-balancing/services';

/**
 * Private cell card for a single shift in the {@link ScheduleCalendar}. Renders the assigned
 * worker (or an open slot), its time, and the source/state encoding documented in the grid legend.
 * When drag is enabled, an assigned card is a drag source (reassignment) and an open slot is a drop
 * target that highlights green/coral on hover.
 */
export function ShiftCalendarCard({
  shift,
  timeLabel,
  highlighted = false,
  available = false,
  assigned = false,
  dragEnabled = false,
  attention = false,
  onShiftClick,
  onWorkerClick,
  onShiftDragStart,
  onShiftDragEnd,
  onShiftDrop,
  getDropValidity,
}: {
  shift: ScheduleShiftType;
  timeLabel?: string;
  /** Cross-highlight ring (e.g. the shift targeted by the selected activity move). */
  highlighted?: boolean;
  /** Open shift a highlighted worker could pick up — grass ring (Team tab). */
  available?: boolean;
  /** Shift already assigned to the highlighted worker — elm fill (Team tab "Show assigned shifts"). */
  assigned?: boolean;
  /** Enables drag-to-assign: assigned cards become draggable, open slots become drop targets. */
  dragEnabled?: boolean;
  /** Cell flagged by a "Needs Attention" card's "Show on Schedule" — coral ring. */
  attention?: boolean;
  onShiftClick?: (shiftId: string) => void;
  onWorkerClick?: (shiftId: string) => void;
  /** Drag an assigned worker off this shift (grid reassignment source). */
  onShiftDragStart?: (shiftId: string) => void;
  onShiftDragEnd?: () => void;
  /** Drop the dragged worker onto this open slot. */
  onShiftDrop?: (shiftId: string) => void;
  /** Validity of dropping the in-flight worker onto this open slot, evaluated on hover. */
  getDropValidity?: (shiftId: string) => ScheduleDropValidityType;
}) {
  const isOpen = !shift.workerName;
  const muted = shift.source === 'pto' || shift.fixedCommitment;
  const nameColor =
    isOpen || muted ? Colors.midnightLighter : Colors.textDefault;
  const badge = sourceBadge(shift);

  // A pending Works suggestion is resolved through accept/reject, not drag (WORK-6399), so it is
  // never a drag source — only confirmed/manual assignments can be moved.
  const isDraggable = dragEnabled && !isOpen && !muted && !shift.suggested;
  const isDropTarget = dragEnabled && isOpen && !muted;
  const [dropValidity, setDropValidity] =
    useState<ScheduleDropValidityType | null>(null);

  const handleDragStart = (event: React.DragEvent) => {
    startDragGhost(event, shift.workerName ?? '');
    onShiftDragStart?.(shift.id);
  };

  const handleDragEnd = () => onShiftDragEnd?.();

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDropValidity(getDropValidity?.(shift.id) ?? null);
  };

  const handleDragLeave = () => setDropValidity(null);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const validity = getDropValidity?.(shift.id);
    setDropValidity(null);
    // An invalid drop is a no-op — the worker is not assigned to a slot they fail.
    if (validity && !validity.valid) return;
    onShiftDrop?.(shift.id);
  };

  return (
    <div
      className="schedule-shift-card"
      role="button"
      tabIndex={0}
      data-shift-id={shift.id}
      style={cardVisualStyle(
        shift,
        highlighted,
        available,
        assigned,
        dropValidity,
        attention
      )}
      draggable={isDraggable || undefined}
      onClick={() => onShiftClick?.(shift.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onShiftClick?.(shift.id);
      }}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onDragEnd={isDraggable ? handleDragEnd : undefined}
      onDragOver={isDropTarget ? handleDragOver : undefined}
      onDragLeave={isDropTarget ? handleDragLeave : undefined}
      onDrop={isDropTarget ? handleDrop : undefined}
    >
      <Gap size="xs" justify="space-between" align="center">
        <span
          className="schedule-shift-card__name"
          style={{ color: nameColor }}
          role={onWorkerClick && !isOpen ? 'link' : undefined}
          onClick={
            onWorkerClick && !isOpen
              ? (event) => {
                  event.stopPropagation();
                  onWorkerClick(shift.id);
                }
              : undefined
          }
        >
          {shift.workerName ?? 'Open'}
        </span>
        {/*
          WORK-6449: the sparkle is a persistent "Works assigned" marker keyed on the
          shift's origin (source === 'balancer'), NOT on `suggested`. `suggested` is the
          transient "pending review while awaiting_review" flag, so gating on it dropped the
          sparkle once a run completed. Source stays 'balancer' regardless of run state, and
          a manual assignment is source 'manual' (WORK-6505), so it correctly shows none.
        */}
        {shift.source === 'balancer' ? (
          <span className="schedule-shift-card__icon">
            <Icon
              kind="light"
              icon="sparkles"
              size="sm"
              color="midnightRegular"
            />
          </span>
        ) : null}
        {badge ? (
          <span className="schedule-shift-card__badge">
            <Text size="sm" weight="medium" color="midnightRegular">
              {badge}
            </Text>
          </span>
        ) : null}
      </Gap>
      {timeLabel ? (
        <Text size="sm" weight="medium" secondary>
          {timeLabel}
        </Text>
      ) : null}
      {shift.source === 'pto' ? (
        <Text size="sm" weight="medium" secondary>
          PTO
        </Text>
      ) : null}
      {dropValidity && !dropValidity.valid && dropValidity.reason ? (
        <Text size="sm" weight="medium" color="coral">
          {dropValidity.reason}
        </Text>
      ) : null}

      <style jsx>{`
        .schedule-shift-card {
          border-radius: ${Radius.sm};
          width: 100%;
          padding: ${Spacing.sm}px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: ${Spacing.xxs}px;
          user-select: none;
          transition:
            box-shadow 0.12s ease,
            border-color 0.12s ease,
            opacity 0.12s ease;
        }
        .schedule-shift-card:hover {
          filter: brightness(0.94);
          outline: 1px solid ${Colors.steelDarker};
          outline-offset: -1px;
        }
        .schedule-shift-card__name {
          flex: 1;
          min-width: 0;
          font-size: ${FontSize.sm};
          font-weight: ${FontWeight.medium};
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .schedule-shift-card__icon {
          flex-shrink: 0;
          line-height: 0;
        }
        .schedule-shift-card__badge {
          flex-shrink: 0;
          line-height: 1;
        }
      `}</style>
    </div>
  );
}

function sourceBadge(shift: ScheduleShiftType): string | null {
  if (!shift.workerName) return null;
  if (shift.source === 'self') return 'S';
  if (shift.source === 'pattern') return 'P';
  if (shift.source === 'float') return 'F';

  return null;
}

// Resolves the state-driven fill / border / left-accent of a shift card, mirroring the grid
// prototype: PTO & fixed commitments read as muted blocks, Works suggestions as amber dashed,
// open slots as a dashed coverage gap, and pattern/self cards carry a colored left stripe.
// `highlighted` adds the elm cross-highlight ring (same ring as the activity selection);
// `available` adds the grass ring marking an open shift the highlighted worker could pick up;
// `assigned` adds an elm fill marking a shift already held by the highlighted worker;
// `dropValidity` (during a drag hover) adds a grass/coral ring; `attention` adds the coral ring
// marking a cell a "Needs Attention" card points at (see {@link ringStyle}).
function cardVisualStyle(
  shift: ScheduleShiftType,
  highlighted: boolean,
  available: boolean,
  assigned: boolean,
  dropValidity: ScheduleDropValidityType | null,
  attention: boolean
): React.CSSProperties {
  const isOpen = !shift.workerName;
  const muted = shift.source === 'pto' || shift.fixedCommitment;

  const resolve = (): {
    background: string;
    borderColor: string;
    borderStyle: 'solid' | 'dashed';
  } => {
    // Assigned shifts belonging to the highlighted worker get an elm fill so the
    // scheduler can see where this person's time already sits across the grid. This
    // wins over the muted PTO/fixed-commitment style: those cells still count as the
    // worker's time, and "Show assigned shifts" must mark them too (WORK-8877) — the
    // PTO label / muted name color keep the block recognizable.
    if (assigned) {
      return {
        background: Colors.elmLightest,
        borderColor: Colors.elmLighter,
        borderStyle: 'solid',
      };
    }
    if (muted) {
      return {
        background: Colors.steelLightest,
        borderColor: Colors.steelLighter,
        borderStyle: 'solid',
      };
    }
    if (isOpen) {
      return {
        background: Colors.white,
        borderColor: Colors.steelDarker,
        borderStyle: 'dashed',
      };
    }
    return {
      background: Colors.white,
      borderColor: Colors.steel,
      borderStyle: 'solid',
    };
  };

  const { background, borderColor, borderStyle } = resolve();

  const leftAccent = (() => {
    if (shift.source === 'pattern' && !isOpen) return Colors.steelDarkest;
    if (shift.source === 'self' && !isOpen) return Colors.elmLighter;
    return null;
  })();

  return {
    background,
    borderTop: `1px ${borderStyle} ${borderColor}`,
    borderRight: `1px ${borderStyle} ${borderColor}`,
    borderBottom: `1px ${borderStyle} ${borderColor}`,
    borderLeft: leftAccent
      ? `3px solid ${leftAccent}`
      : `1px ${borderStyle} ${borderColor}`,
    ...ringStyle(highlighted, available, dropValidity, attention),
  };
}

// The ring drawn around a card. A live drop-hover wins — and gets a thicker ring plus a tinted fill
// so the hovered target stands out from sibling available shifts (which share the thin grass ring),
// preventing accidental drops on a neighbour. Otherwise the elm cross-highlight (activity selection)
// wins, then the coral "Needs Attention" ring, then the grass availability ring; none set ⇒ no ring.
function ringStyle(
  highlighted: boolean,
  available: boolean,
  dropValidity: ScheduleDropValidityType | null,
  attention: boolean
): React.CSSProperties {
  if (dropValidity) {
    const color = dropValidity.valid ? Colors.grass : Colors.coral;
    const fill = dropValidity.valid
      ? Colors.grassLightest
      : Colors.coralLightest;
    return { boxShadow: `0 0 0 3px ${color}`, background: fill };
  }
  if (highlighted) return { boxShadow: `0 0 0 2px ${Colors.elm}` };
  if (attention) return { boxShadow: `0 0 0 2px ${Colors.coral}` };
  if (available) return { boxShadow: `0 0 0 2px ${Colors.grass}` };
  return {};
}
